import { useState } from "react";
import {
  LEAD_LOSS_REASONS,
  NOT_APPLICABLE_REASONS,
} from "../utils/displayFormatters";

const LossReasonModal = ({
  isOpen,
  onClose,
  onSave,
  status,
  category,
  initialReason = "",
  initialNotes = "",
}) => {
  const [lossReason, setLossReason] = useState(initialReason);
  const [lossReasonNotes, setLossReasonNotes] = useState(initialNotes);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    if (!lossReason) {
      alert("Please select a reason before saving.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(status, category, lossReason, lossReasonNotes);
      onClose();
    } catch (error) {
      console.error("Error saving loss reason:", error);
      alert("Failed to save. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setLossReason(initialReason);
    setLossReasonNotes(initialNotes);
    onClose();
  };

  if (!isOpen) return null;

  const isNotApplicable = category === "not_applicable";
  const statusLabel = isNotApplicable ? "Not Applicable" : "Not Interested";
  const isEditing = initialReason && initialReason.length > 0;
  const reasonOptions = isNotApplicable ? NOT_APPLICABLE_REASONS : LEAD_LOSS_REASONS;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <svg
              className="h-6 w-6 text-red-600 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-800">
              {isEditing
                ? `Edit ${statusLabel} Reason`
                : `Mark as ${statusLabel}`}
            </h3>
          </div>

          <p className="text-gray-600 mb-4">
            {isEditing
              ? `Update the reason for why this lead is "${statusLabel.toLowerCase()}":`
              : `Please provide a reason for marking this lead as "${statusLabel.toLowerCase()}":`}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-red-700 text-sm font-medium mb-2">
                Reason *
              </label>
              <select
                className="w-full border-2 border-red-300 rounded-lg py-2 px-3 text-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={lossReason}
                onChange={(e) => setLossReason(e.target.value)}
                required
              >
                <option value="">Select a reason</option>
                {reasonOptions.map((reason) => (
                  <option key={reason.key} value={reason.key}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-red-700 text-sm font-medium mb-2">
                Additional Notes
              </label>
              <textarea
                className="w-full border-2 border-red-300 rounded-lg py-2 px-3 text-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows="3"
                value={lossReasonNotes}
                onChange={(e) => setLossReasonNotes(e.target.value)}
                placeholder="Provide additional context for the loss reason..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-border rounded-lg text-gray-700 hover:bg-background focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-300"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              data-attr="lead-mark-lost"
              onClick={handleSave}
              disabled={isSubmitting || !lossReason}
              className={`px-4 py-2 rounded-lg text-white font-medium focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-300 ${
                isSubmitting || !lossReason
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {isSubmitting
                ? "Saving..."
                : isEditing
                  ? "Update Reason"
                  : `Mark as ${statusLabel}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LossReasonModal;
